<template>
  <div class="fixed inset-0 bg-gray-900 bg-opacity-70 flex items-center justify-center p-4 z-50 transition-opacity duration-300 animate-modalFade">
    <div class="w-full max-w-lg bg-gray-800 text-white rounded-lg p-5 flex flex-col gap-4 relative shadow-xl animate-scaleIn">
      <!-- Header -->
      <div class="flex items-center justify-between border-b border-gray-700 pb-3">
        <div class="flex items-center gap-3">
          <img
            :src="data?.img || defaultImg"
            alt="Robot Image"
            class="w-12 h-12 sm:w-16 sm:h-16 rounded-md object-cover border-2"
            :class="data?.rarityColors?.borderColor || 'border-indigo-400'"
            @error="handleImageError"
          />

          <div>
            <h1
              class="text-lg sm:text-xl font-semibold"
              :class="data?.rarityColors?.textColor || 'text-indigo-200'"
            >
              {{ data?.name || 'ไม่ระบุ' }}
            </h1>

            <p class="text-indigo-200 text-xs sm:text-sm">
              ประเภท: หุ่นยนต์สังหาร
            </p>

            <div v-if="data?.rarity" class="flex items-center gap-1 mt-1">
              <template v-for="star in Number(data.rarity)" :key="star">
                <i class="fas fa-star text-yellow-400 text-xs sm:text-sm"></i>
              </template>
            </div>
          </div>
        </div>

        <button
          @click="$emit('close')"
          class="text-gray-300 hover:text-indigo-300 group"
          aria-label="ปิด"
        >
          <i class="fas fa-times text-base group-hover:scale-110 transition-transform duration-200"></i>
        </button>
      </div>

      <!-- Notification Message -->
      <div
        v-if="notificationMessage"
        class="text-center text-sm"
        :class="notificationType === 'success' ? 'text-green-400' : 'text-red-400'"
      >
        {{ notificationMessage }}
      </div>

      <!-- Tab Navigation -->
      <div class="flex border-b border-gray-700">
        <button
          @click="activeTab = 'info'"
          :class="[
            'px-4 py-2 text-sm font-medium flex-1',
            activeTab === 'info'
              ? 'border-b-2 border-indigo-500 text-indigo-300'
              : 'text-gray-400 hover:text-white'
          ]"
        >
          ข้อมูล
        </button>

        <button
          @click="activeTab = 'upgrade'"
          :class="[
            'px-4 py-2 text-sm font-medium flex-1',
            activeTab === 'upgrade'
              ? 'border-b-2 border-indigo-500 text-indigo-300'
              : 'text-gray-400 hover:text-white'
          ]"
          :disabled="isDeployed"
        >
          อัปเกรด
        </button>

        <button
          @click="activeTab = 'deploy'"
          :class="[
            'px-4 py-2 text-sm font-medium flex-1',
            activeTab === 'deploy'
              ? 'border-b-2 border-indigo-500 text-indigo-300'
              : 'text-gray-400 hover:text-white'
          ]"
        >
          ใช้งาน
        </button>
      </div>

      <!-- Tab Content -->
      <div class="max-h-96 overflow-y-auto scrollbar-thin px-2">
        <!-- Info Tab -->
        <div v-if="activeTab === 'info'" class="space-y-4">
          <section>
            <h2 class="text-sm font-semibold text-indigo-200 mb-2">
              สถานะปัจจุบัน
            </h2>

            <div class="grid grid-cols-2 gap-3 text-sm">
              <div class="bg-gray-700 p-2 rounded-md">
                <i class="fas fa-fist-raised text-indigo-400 mr-1"></i>
                โจมตี: {{ calculatedStats.atk }}
              </div>

              <div class="bg-gray-700 p-2 rounded-md">
                <i class="fas fa-bolt text-indigo-400 mr-1"></i>
                คริติคอล: {{ calculatedStats.critical }}%
              </div>

              <div class="bg-gray-700 p-2 rounded-md">
                <i class="fas fa-wind text-indigo-400 mr-1"></i>
                หลบหลีก: {{ calculatedStats.evasion }}%
              </div>

              <div class="bg-gray-700 p-2 rounded-md">
                <i class="fas fa-shield-alt text-indigo-400 mr-1"></i>
                ป้องกัน: {{ calculatedStats.defense }}
              </div>
            </div>
          </section>

          <!-- HP Status Section -->
          <section>
            <h2 class="text-sm font-semibold text-indigo-200 mb-2">
              พลังชีวิต
            </h2>

            <div class="relative bg-gray-700 rounded-md p-3">
              <div class="bg-gray-600 h-5 rounded overflow-hidden mb-2">
                <div
                  class="h-5 rounded transition-all duration-200"
                  :class="hpPercentage > 50 ? 'bg-indigo-500' : hpPercentage > 20 ? 'bg-yellow-500' : 'bg-red-500'"
                  :style="{ width: `${hpPercentage}%` }"
                ></div>
              </div>

              <p class="text-sm text-center text-indigo-200">
                HP: {{ currentHp }} / {{ calculatedStats.maxHp }}
                (เสียหาย: {{ data.damageTaken || 0 }})
              </p>

              <button
                v-if="!isDeployed && data.damageTaken > 0"
                @click="repairRobot"
                class="w-full mt-2 bg-blue-600 hover:bg-blue-700 text-sm text-white py-2 rounded-md transition-colors duration-200 flex items-center justify-center gap-2"
                :disabled="isRepairing"
              >
                <span v-if="!isRepairing">
                  <i class="fas fa-medkit mr-1 text-xs"></i>
                  รักษาหุ่นยนต์ (5000 <i class="fa-solid fa-coins text-white"></i>)
                </span>
                <div v-else class="animate-spin rounded-full h-4 w-4 border-t-2 border-white"></div>
              </button>
            </div>
          </section>

          <!-- Remaining Upgrades Section -->
          <section>
            <h2 class="text-sm font-semibold text-indigo-200 mb-2">
              สถานะการอัปเกรด
            </h2>

            <div class="bg-gray-700 p-3 rounded-md text-center">
              <p class="text-sm text-indigo-200">
                อัปเกรดเหลือ: {{ remainingUpgrades }} ครั้ง
              </p>
            </div>
          </section>

          <div v-if="isDeployed">
            <p class="text-sm text-indigo-200">
              กำลังทำงานที่: {{ data.map }}
            </p>
          </div>
        </div>

        <!-- Upgrade Tab -->
        <div v-if="activeTab === 'upgrade'" class="space-y-4">
          <!-- Upgrade Type Toggle -->
          <div class="flex gap-2">
            <button
              @click="upgradeType = 'item'"
              :class="[
                'flex-1 py-2 text-sm font-medium',
                upgradeType === 'item'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-700 text-gray-300'
              ]"
              class="rounded-md transition-colors"
            >
              อัปเกรดด้วยไอเทม
            </button>

            <button
              @click="upgradeType = 'silver'"
              :class="[
                'flex-1 py-2 text-sm font-medium',
                upgradeType === 'silver'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-700 text-gray-300'
              ]"
              class="rounded-md transition-colors"
            >
              อัปเกรดด้วย <i class="fa-solid fa-coins text-white"></i>
            </button>
          </div>

          <div v-if="upgradeType === 'item'">
            <div v-if="isRedistributeMode">
              <h2 class="text-sm font-semibold text-indigo-200 mb-2">
                กระจาย {{ statPoints }} แต้มใหม่
              </h2>

              <div class="grid grid-cols-4 gap-2">
                <div
                  v-for="stat in statList"
                  :key="stat.name"
                  class="flex flex-col items-center bg-gray-700 p-2 rounded-md"
                >
                  <i :class="['text-indigo-400 mb-1 text-lg', stat.icon]"></i>
                  <span class="text-indigo-200 text-xs mb-1 capitalize">
                    {{ stat.name }}
                  </span>

                  <div class="flex items-center gap-1 w-full">
                    <button
                      @click="decreasePoint(stat.name)"
                      class="flex-1 bg-gray-600 py-0.5 rounded hover:bg-gray-500 text-sm"
                    >
                      -
                    </button>

                    <input
                      type="number"
                      v-model.number="points[stat.name]"
                      class="w-8 text-center bg-gray-600 py-0.5 rounded text-sm"
                      min="0"
                      step="1"
                      spellcheck="false"
                      @input="clampPoint(stat.name)"
                    />

                    <button
                      @click="increasePoint(stat.name)"
                      class="flex-1 bg-gray-600 py-0.5 rounded hover:bg-gray-500 text-sm"
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>

              <p
                v-if="totalPoints !== statPoints"
                class="text-red-300 text-xs mt-2 text-center"
              >
                ต้องกระจายครบ {{ statPoints }} แต้ม
              </p>
            </div>

            <div v-else>
              <div v-if="maxPossibleUpgrades > 0">
                <h2 class="text-sm font-semibold text-indigo-200 mb-2">
                  กระจาย {{ requiredPoints }} แต้ม
                  (อัปเกรด {{ maxPossibleUpgrades }} ครั้ง)
                  (เหลือ {{ remainingUpgrades }} ครั้ง)
                </h2>

                <div class="grid grid-cols-4 gap-2">
                  <div
                    v-for="stat in statList"
                    :key="stat.name"
                    class="flex flex-col items-center bg-gray-700 p-2 rounded-md"
                  >
                    <i :class="['text-indigo-400 mb-1 text-lg', stat.icon]"></i>
                    <span class="text-indigo-200 text-xs mb-1 capitalize">
                      {{ stat.name }}
                    </span>

                    <div class="flex items-center gap-1 w-full">
                      <button
                        @click="decreasePoint(stat.name)"
                        class="flex-1 bg-gray-600 py-0.5 rounded hover:bg-gray-500 text-sm"
                      >
                        -
                      </button>

                      <input
                        type="number"
                        v-model.number="points[stat.name]"
                        class="w-8 text-center bg-gray-600 py-0.5 rounded text-sm"
                        min="0"
                        step="1"
                        spellcheck="false"
                        @input="clampPoint(stat.name)"
                      />

                      <button
                        @click="increasePoint(stat.name)"
                        class="flex-1 bg-gray-600 py-0.5 rounded hover:bg-gray-500 text-sm"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>

                <p
                  v-if="totalPoints !== requiredPoints"
                  class="text-red-300 text-xs mt-2 text-center"
                >
                  ต้องกระจายครบ {{ requiredPoints }} แต้ม
                  ({{ maxPossibleUpgrades }} ครั้ง)
                </p>
              </div>

              <!-- Materials Required -->
              <div v-if="remainingUpgrades > 0" class="mt-4">
                <h3 class="text-sm font-semibold text-indigo-200 mb-2">
                  ไอเทมที่ต้องใช้ต่อการอัปเกรด
                </h3>

                <div class="grid grid-cols-2 gap-2">
                  <div
                    v-for="(material, index) in data.upgradeMaterials"
                    :key="index"
                    class="flex justify-between items-center text-sm bg-gray-700 p-2 rounded-md border"
                    :class="hasEnoughForOne(material) ? 'border-green-500' : 'border-red-500'"
                  >
                    <span class="text-indigo-200">
                      {{ material.itemName }}
                    </span>

                    <span :class="hasEnoughForOne(material) ? 'text-green-400' : 'text-red-400'">
                      {{ getMaterialCount(material.itemName) }} / {{ material.quantity }}
                    </span>
                  </div>
                </div>

                <p class="text-sm text-yellow-300 mt-2 text-center">
                  สามารถอัปเกรดได้ {{ maxPossibleUpgrades }} ครั้ง ด้วยวัสดุปัจจุบัน
                </p>
              </div>

              <div v-if="maxPossibleUpgrades === 0">
                <div v-if="remainingUpgrades > 0" class="text-center text-yellow-300 py-4">
                  ไม่มีวัสดุเพียงพอสำหรับอัปเกรด
                </div>

                <div v-else class="text-center text-yellow-300 py-4">
                  อัปเกรดได้สูงสุดแล้ว
                </div>
              </div>
            </div>

            <!-- Preview Changes -->
            <div v-if="isRedistributeMode || maxPossibleUpgrades > 0" class="mt-4">
              <h3 class="text-sm font-semibold text-indigo-200 mb-2">
                ตัวอย่างหลังอัปเกรด
              </h3>

              <div class="grid grid-cols-2 gap-3 text-sm">
                <div class="bg-gray-700 p-2 rounded-md flex flex-col">
                  <span class="text-gray-400 text-xs">พลังชีวิต</span>
                  <span>
                    {{ previewStats.maxHp }}
                    <span class="text-green-400 text-xs">
                      (+{{ previewStats.maxHp - calculatedStats.maxHp }})
                    </span>
                  </span>
                </div>

                <div class="bg-gray-700 p-2 rounded-md flex flex-col">
                  <span class="text-gray-400 text-xs">โจมตี</span>
                  <span>
                    {{ previewStats.atk }}
                    <span class="text-green-400 text-xs">
                      (+{{ previewStats.atk - calculatedStats.atk }})
                    </span>
                  </span>
                </div>

                <div class="bg-gray-700 p-2 rounded-md flex flex-col">
                  <span class="text-gray-400 text-xs">คริติคอล</span>
                  <span>
                    {{ previewStats.critical }}%
                    <span class="text-green-400 text-xs">
                      (+{{ previewStats.critical - calculatedStats.critical }})
                    </span>
                  </span>
                </div>

                <div class="bg-gray-700 p-2 rounded-md flex flex-col">
                  <span class="text-gray-400 text-xs">หลบหลีก</span>
                  <span>
                    {{ previewStats.evasion }}%
                    <span class="text-green-400 text-xs">
                      (+{{ previewStats.evasion - calculatedStats.evasion }})
                    </span>
                  </span>
                </div>

                <div class="bg-gray-700 p-2 rounded-md flex flex-col">
                  <span class="text-gray-400 text-xs">ป้องกัน</span>
                  <span>
                    {{ previewStats.defense }}
                    <span class="text-green-400 text-xs">
                      (+{{ previewStats.defense - calculatedStats.defense }})
                    </span>
                  </span>
                </div>

                <div
                  v-if="!isRedistributeMode"
                  class="bg-gray-700 p-2 rounded-md flex flex-col"
                >
                  <span class="text-gray-400 text-xs">Rarity</span>
                  <span>
                    {{ Number(data.rarity || 0) + maxPossibleUpgrades }}
                    <span class="text-green-400 text-xs">
                      (+{{ maxPossibleUpgrades }})
                    </span>
                  </span>
                </div>
              </div>
            </div>

            <button
              v-if="isRedistributeMode || maxPossibleUpgrades > 0"
              @click="handleUpgradeOrRedistribute"
              class="w-full mt-4 bg-indigo-600 hover:bg-indigo-700 text-sm text-white py-2 rounded-md transition-colors duration-200 flex items-center justify-center gap-2"
              :disabled="isUpgrading || totalPoints !== requiredPoints || (!isRedistributeMode && !hasEnoughMaterialsForMulti)"
            >
              <span v-if="!isUpgrading">
                <i class="fas fa-arrow-up mr-1 text-xs"></i>
                {{ isRedistributeMode ? 'ยืนยันการกระจายใหม่' : 'อัปเกรด' }}
              </span>
              <div v-else class="animate-spin rounded-full h-4 w-4 border-t-2 border-white"></div>
            </button>

            <!-- Reset Button -->
            <button
              v-if="!isRedistributeMode && totalItemPoints > 0"
              @click="resetRobotStats"
              class="w-full mt-4 bg-yellow-600 hover:bg-yellow-700 text-sm text-white py-2 rounded-md transition-colors duration-200 flex items-center justify-center gap-2"
              :disabled="isResetting || silver < 10000"
            >
              <span v-if="!isResetting">
                <i class="fas fa-sync-alt mr-1 text-xs"></i>
                รีเซ็ตสเตตัส (10000 silver)
              </span>
              <div v-else class="animate-spin rounded-full h-4 w-4 border-t-2 border-white"></div>
            </button>
          </div>

          <!-- Silver Upgrade Section -->
          <div v-if="upgradeType === 'silver'" class="mt-4">
            <h3 class="text-sm font-semibold text-indigo-200 mb-2">
              <i class="fa-solid fa-coins text-white"></i>
              ปัจจุบัน: {{ silver }}
            </h3>

            <div class="space-y-3">
              <div
                v-for="upgrade in silverUpgrades"
                :key="upgrade.id"
                class="bg-gray-700 p-3 rounded-md flex justify-between items-center"
              >
                <div>
                  <p class="text-sm text-indigo-200">
                    {{ upgrade.name }}
                  </p>
                  <p class="text-xs text-gray-400">
                    {{ upgrade.description }}
                  </p>
                </div>

                <button
                  v-if="!isSilverUpgradePurchased(upgrade.id)"
                  @click="purchaseSilverUpgrade(upgrade)"
                  class="bg-purple-600 hover:bg-purple-700 text-xs text-white py-1 px-3 rounded-md transition-colors duration-200 flex items-center gap-2"
                  :disabled="isPurchasing[upgrade.id] || silver < upgrade.cost"
                >
                  <span v-if="!isPurchasing[upgrade.id]">
                    ซื้อ ({{ upgrade.cost }} <i class="fa-solid fa-coins text-white"></i>)
                  </span>
                  <div v-else class="animate-spin rounded-full h-3 w-3 border-t-2 border-white"></div>
                </button>

                <span v-else class="text-green-400 text-xs">
                  อัปเกรดแล้ว
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Deploy Tab -->
        <div v-if="activeTab === 'deploy'" class="space-y-4">
          <div v-if="isDeployed">
            <h2 class="text-sm font-semibold text-indigo-200 mb-2">
              หุ่นยนต์กำลังทำงานที่ {{ data.map }}
            </h2>

            <button
              @click="recallRobot"
              class="w-full bg-red-600 hover:bg-red-700 text-sm text-white py-2 rounded-md transition-colors duration-200 flex items-center justify-center gap-2"
              :disabled="isRecalling"
            >
              <span v-if="!isRecalling">
                <i class="fas fa-undo mr-1 text-xs"></i>
                เรียกคืนหุ่นยนต์
              </span>
              <div v-else class="animate-spin rounded-full h-4 w-4 border-t-2 border-white"></div>
            </button>
          </div>

          <div v-else>
            <h2 class="text-sm font-semibold text-indigo-200 mb-2">
              เลือกแผนที่สำหรับออกล่า
            </h2>

            <select
              v-model="selectedMap"
              class="w-full bg-gray-700 text-indigo-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 border border-gray-600"
            >
              <option value="">เลือกแผนที่</option>

              <option
                v-for="mapName in allowedMaps"
                :key="mapName"
                :value="mapName"
              >
                {{ mapName }}
              </option>
            </select>

            <p class="text-sm text-yellow-300 mt-2 text-center">
              <i class="fas fa-exclamation-triangle mr-1"></i>
              เมื่อสั่งให้หุ่นยนต์ไปทำงาน ต้องรออีก 3 ชั่วโมงจึงจะเรียกกลับมาได้
            </p>

            <button
              @click="confirmDeploy"
              class="w-full bg-green-600 hover:bg-green-700 text-sm text-white py-2 rounded-md transition-colors duration-200 flex items-center justify-center gap-2"
              :disabled="isDeploying || !selectedMap"
            >
              <span v-if="!isDeploying">
                <i class="fas fa-play mr-1 text-xs"></i>
                ใช้งานหุ่นยนต์
              </span>
              <div v-else class="animate-spin rounded-full h-4 w-4 border-t-2 border-white"></div>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { projectFunctions, projectFirestore } from '@/firebase/config';
import getUser from '@/composables/getUser';
import defaultImg from '@/assets/default-item.png';
import gameItems from '@/data/gameItems.json';

export default {
  name: 'KillerRobotModal',

  props: {
    itemId: {
      type: String,
      required: true,
    },
    inventoryItems: {
      type: Array,
      required: true,
    },
    item: {
      type: Object,
      default: null,
    },
    states: {
      type: Object,
      default: () => ({}),
    },
  },

  emits: ['close', 'robot-updated'],

  setup(props, { emit }) {
    const { user } = getUser();

    const activeTab = ref('info');
    const upgradeType = ref('item');
    const points = ref({ stamina: 0, str: 0, int: 0, agi: 0 });

    const isUpgrading = ref(false);
    const isResetting = ref(false);
    const isDeploying = ref(false);
    const isRecalling = ref(false);
    const isRepairing = ref(false);
    const isPurchasing = ref({});

    const selectedMap = ref('');
    const notificationMessage = ref('');
    const notificationType = ref('success');

    const silver = ref(0);
    const data = ref({});

    let unsubscribeSilver = null;
    let unsubscribeBagItem = null;

    const num = (value, fallback = 0) => {
      const n = Number(value);
      return Number.isFinite(n) ? n : fallback;
    };

    const asArray = (value) => {
      return Array.isArray(value) ? value : [];
    };

    const asObject = (value) => {
      return value && typeof value === 'object' && !Array.isArray(value)
        ? value
        : {};
    };

    const normalizeStats = (stats = {}) => {
      const safe = asObject(stats);

      return {
        stamina: num(safe.stamina, 0),
        str: num(safe.str, 0),
        int: num(safe.int, 0),
        agi: num(safe.agi, 0),
        defense: num(safe.defense, 0),
        maxHp: num(safe.maxHp, 0),
        atk: num(safe.atk, 0),
        critical: num(safe.critical, 0),
        evasion: num(safe.evasion, 0),
      };
    };

    const mergeStats = (...statsList) => {
      const result = {
        stamina: 0,
        str: 0,
        int: 0,
        agi: 0,
        defense: 0,
        maxHp: 0,
        atk: 0,
        critical: 0,
        evasion: 0,
      };

      statsList.forEach((stats) => {
        const safe = normalizeStats(stats);
        Object.keys(result).forEach((key) => {
          result[key] += num(safe[key], 0);
        });
      });

      return result;
    };

    const getCurrentInstanceId = () => {
      return data.value?.instanceId || props.item?.instanceId || props.item?.id || props.itemId;
    };

    const getTemplate = (item) => {
      const templateId = item?.templateId || item?.states?.templateId || item?.name;
      return gameItems?.[templateId] || gameItems?.[item?.name] || {};
    };

    const silverUpgrades = [
      { id: 7, name: 'เพิ่มความแข็งแกร่ง I', description: '+50 stamina', cost: 10000, effect: { stamina: 50 } },
      { id: 8, name: 'เพิ่มความแข็งแกร่ง II', description: '+50 stamina', cost: 15000, effect: { stamina: 50 } },
      { id: 9, name: 'เพิ่มความแข็งแกร่ง III', description: '+50 stamina', cost: 20000, effect: { stamina: 50 } },
      { id: 10, name: 'เพิ่มความแข็งแกร่ง IV', description: '+50 stamina', cost: 25000, effect: { stamina: 50 } },
      { id: 11, name: 'เพิ่มความแข็งแกร่ง V', description: '+50 stamina', cost: 30000, effect: { stamina: 50 } },

      { id: 6, name: 'อัปเกรดอาวุธ', description: '+10 Str', cost: 10000, effect: { str: 10 } },

      { id: 12, name: 'เพิ่มความเร็ว I', description: '+10 agi', cost: 1000, effect: { agi: 10 } },
      { id: 13, name: 'เพิ่มความเร็ว II', description: '+10 agi', cost: 3000, effect: { agi: 10 } },
      { id: 14, name: 'เพิ่มความเร็ว III', description: '+10 agi', cost: 5000, effect: { agi: 10 } },
      { id: 15, name: 'เพิ่มความเร็ว IV', description: '+10 agi', cost: 7000, effect: { agi: 10 } },
      { id: 16, name: 'เพิ่มความเร็ว V', description: '+10 agi', cost: 10000, effect: { agi: 10 } },

      { id: 17, name: 'เพิ่มพลังปัญญา I', description: '+10 int', cost: 1000, effect: { int: 10 } },
      { id: 18, name: 'เพิ่มพลังปัญญา II', description: '+10 int', cost: 3000, effect: { int: 10 } },
      { id: 19, name: 'เพิ่มพลังปัญญา III', description: '+10 int', cost: 5000, effect: { int: 10 } },
      { id: 20, name: 'เพิ่มพลังปัญญา IV', description: '+10 int', cost: 7000, effect: { int: 10 } },
      { id: 21, name: 'เพิ่มพลังปัญญา V', description: '+10 int', cost: 10000, effect: { int: 10 } },

      { id: 1, name: 'เกราะ I', description: '+5 defense', cost: 1000, effect: { defense: 5 } },
      { id: 2, name: 'เกราะ II', description: '+5 defense', cost: 3000, effect: { defense: 5 } },
      { id: 3, name: 'เกราะ III', description: '+5 defense', cost: 5000, effect: { defense: 5 } },
      { id: 4, name: 'เกราะ IV', description: '+5 defense', cost: 7000, effect: { defense: 5 } },
      { id: 5, name: 'เกราะ V', description: '+5 defense', cost: 10000, effect: { defense: 5 } },
      { id: 22, name: 'เกราะ VI', description: '+5 defense', cost: 10000, effect: { defense: 5 } },
      { id: 23, name: 'เกราะ VII', description: '+5 defense', cost: 10000, effect: { defense: 5 } },
      { id: 24, name: 'เกราะ VIII', description: '+5 defense', cost: 10000, effect: { defense: 5 } },
      { id: 25, name: 'เกราะ IX', description: '+5 defense', cost: 10000, effect: { defense: 5 } },
      { id: 26, name: 'เกราะ X', description: '+5 defense', cost: 10000, effect: { defense: 5 } },
    ];

    const robotMap = [
      {
        name: 'หุ่นยนต์รัสต์แฮมเมอร์',
        map: ['รถไฟฟ้าใต้ดิน', 'โรงพยาบาล', 'หมู่บ้านคนรวย', 'สวนสนุก', 'โรงงานอุตสาหกรรม'],
      },
      {
        name: 'หุ่นยนต์ไคเนติก',
        map: ['รถไฟฟ้าใต้ดิน', 'โรงพยาบาล', 'หมู่บ้านคนรวย', 'สวนสนุก', 'โรงงานอุตสาหกรรม', 'ห้างสรรพสินค้า', 'สถานีดับเพลิงร้าง', 'ค่ายทหาร'],
      },
      {
        name: 'หุ่นยนต์วอยด์',
        map: ['โรงงานอุตสาหกรรม', 'ห้างสรรพสินค้า', 'สถานีดับเพลิงร้าง', 'ค่ายทหาร', 'สถาบันวิจัยหุ่นยนต์'],
      },
      {
        name: 'หุ่นยนต์รามศึก',
        map: ['โรงงานอุตสาหกรรม', 'ห้างสรรพสินค้า', 'สถานีดับเพลิงร้าง', 'ค่ายทหาร', 'สถาบันวิจัยหุ่นยนต์', 'สถาบันวิจัยไวรัส', 'สุสานนักรบโบราณ', 'ตึกความมันคงแห่งชาติ'],
      },
      {
        name: 'หุ่นยนต์สุนัขบางแก้ว',
        map: ['โรงงานอุตสาหกรรม', 'ห้างสรรพสินค้า', 'สถานีดับเพลิงร้าง', 'ค่ายทหาร', 'สถาบันวิจัยหุ่นยนต์', 'สถาบันวิจัยไวรัส', 'สุสานนักรบโบราณ', 'ตึกความมันคงแห่งชาติ'],
      },
      {
        name: 'หุ่นยนต์สุนัขหลังอาน',
        map: ['โรงงานอุตสาหกรรม', 'ห้างสรรพสินค้า', 'สถานีดับเพลิงร้าง', 'ค่ายทหาร', 'สถาบันวิจัยหุ่นยนต์', 'สถาบันวิจัยไวรัส', 'สุสานนักรบโบราณ', 'ตึกความมันคงแห่งชาติ'],
      },
      {
        name: 'หุ่นยนต์หมีขั้วโลก',
        map: ['โรงงานอุตสาหกรรม', 'ห้างสรรพสินค้า', 'สถานีดับเพลิงร้าง', 'ค่ายทหาร', 'สถาบันวิจัยหุ่นยนต์', 'สถาบันวิจัยไวรัส', 'สุสานนักรบโบราณ', 'ตึกความมันคงแห่งชาติ'],
      },
      {
        name: 'หุ่นยนต์พิทบูล',
        map: ['โรงงานอุตสาหกรรม', 'ห้างสรรพสินค้า', 'สถานีดับเพลิงร้าง', 'ค่ายทหาร', 'สถาบันวิจัยหุ่นยนต์', 'สถาบันวิจัยไวรัส', 'สุสานนักรบโบราณ', 'ตึกความมันคงแห่งชาติ'],
      },
      {
        name: 'หุ่นยนต์กอริลล่า',
        map: ['โรงงานอุตสาหกรรม', 'ห้างสรรพสินค้า', 'สถานีดับเพลิงร้าง', 'ค่ายทหาร', 'สถาบันวิจัยหุ่นยนต์', 'สถาบันวิจัยไวรัส', 'สุสานนักรบโบราณ', 'ตึกความมันคงแห่งชาติ'],
      },
      {
        name: 'หุ่นยนต์แอทลาส',
        map: ['สถานีดับเพลิงร้าง', 'ค่ายทหาร', 'สถาบันวิจัยหุ่นยนต์', 'สถาบันวิจัยไวรัส', 'สุสานนักรบโบราณ', 'ตึกความมันคงแห่งชาติ'],
      },
      {
        name: 'หุ่นยนต์มังกรดิน',
        map: ['สถานีดับเพลิงร้าง', 'ค่ายทหาร', 'สถาบันวิจัยหุ่นยนต์', 'สถาบันวิจัยไวรัส', 'สุสานนักรบโบราณ', 'ตึกความมันคงแห่งชาติ'],
      },
      {
        name: 'หุ่นยนต์มังกรไฟ',
        map: ['สถานีดับเพลิงร้าง', 'ค่ายทหาร', 'สถาบันวิจัยหุ่นยนต์', 'สถาบันวิจัยไวรัส', 'สุสานนักรบโบราณ', 'ตึกความมันคงแห่งชาติ'],
      },
      {
        name: 'หุ่นยนต์จิ้งจอกเก้าหาง',
        map: ['สถานีดับเพลิงร้าง', 'ค่ายทหาร', 'สถาบันวิจัยหุ่นยนต์', 'สถาบันวิจัยไวรัส', 'สุสานนักรบโบราณ', 'ตึกความมันคงแห่งชาติ'],
      },
      {
        name: 'หุ่นยนต์ซอมบี้',
        map: ['สถานีดับเพลิงร้าง', 'ค่ายทหาร', 'สถาบันวิจัยหุ่นยนต์', 'สถาบันวิจัยไวรัส', 'สุสานนักรบโบราณ', 'ตึกความมันคงแห่งชาติ'],
      },
      {
        name: 'หุ่นยนต์เสือดำ',
        map: ['สถาบันวิจัยไวรัส', 'สุสานนักรบโบราณ', 'ตึกความมันคงแห่งชาติ', 'โรงเก็บศพจักรกล', 'ตึกปฏิบัติการพันธุกรรม'],
      },
    ];

    const gameActions = projectFunctions.httpsCallable('gameActions');

    const callManageKillerRobot = (subAction, payload = {}) => {
      const instanceId = getCurrentInstanceId();
      if (!instanceId) {
        return Promise.reject(new Error('ไม่พบรหัสไอเทมหุ่นยนต์'));
      }

      return gameActions({
        action: 'manageKillerRobot',
        subAction,
        instanceId,
        itemId: instanceId,
        ...payload,
      });
    };

    const normalizeRobotItem = (item) => {
      const template = getTemplate(item);

      /*
        สำคัญ:
        props.states มาจากตอนเปิด modal และอาจเก่า
        item.states จาก local patch / Firestore snapshot ต้องชนะเสมอ
      */
      const states = {
        ...asObject(props.states),
        ...asObject(item?.states),
      };

      return {
        ...template,
        ...item,
        ...states,

        states,

        id: item?.id || item?.instanceId || props.itemId,
        instanceId: item?.instanceId || item?.id || props.itemId,

        templateId:
          item?.templateId ||
          states.templateId ||
          template.templateId ||
          item?.name,

        name:
          states.name ||
          item?.name ||
          template.name ||
          item?.templateId ||
          'หุ่นยนต์สังหาร',

        type:
          states.type ||
          item?.type ||
          template.type ||
          'หุ่นยนต์สังหาร',

        img:
          states.img ||
          item?.img ||
          template.img ||
          defaultImg,

        stats: normalizeStats(
          states.stats ||
          item?.stats ||
          template.stats ||
          {}
        ),

        itemUpgradeStats: normalizeStats(
          states.itemUpgradeStats ||
          item?.itemUpgradeStats ||
          {}
        ),

        silverUpgradeStats: normalizeStats(
          states.silverUpgradeStats ||
          item?.silverUpgradeStats ||
          {}
        ),

        silverUpgrades: asArray(
          states.silverUpgrades ?? item?.silverUpgrades
        ),

        upgradeMaterials: asArray(
          states.upgradeMaterials ??
          item?.upgradeMaterials ??
          template.upgradeMaterials
        ),

        cardSlots: asArray(
          states.cardSlots ?? item?.cardSlots
        ),

        cardStats: asObject(
          states.cardStats ?? item?.cardStats
        ),

        rarity: num(
          states.rarity ??
          item?.rarity ??
          template.rarity,
          1
        ),

        rarityColors: asObject(
          states.rarityColors ??
          item?.rarityColors ??
          template.rarityColors
        ),

        damageTaken: num(
          states.damageTaken ?? item?.damageTaken,
          0
        ),

        statPoints: num(
          states.statPoints ?? item?.statPoints,
          0
        ),

        upgradeCount: num(
          states.upgradeCount ?? item?.upgradeCount,
          0
        ),

        maxUpgradeCount: num(
          states.maxUpgradeCount ??
          item?.maxUpgradeCount ??
          template.maxUpgradeCount,
          0
        ),

        transfer:
          states.transfer ??
          item?.transfer ??
          template.transfer ??
          false,

        decomposable:
          states.decomposable ??
          item?.decomposable ??
          template.decomposable ??
          true,

        protected:
          states.protected ??
          item?.protected ??
          false,

        map:
          states.map ||
          item?.map ||
          null,

        killerRobotId:
          states.killerRobotId ||
          item?.killerRobotId ||
          null,
      };
    };

    const findRobotItem = () => {
      const instanceId = getCurrentInstanceId();

      if (
        props.item &&
        (props.item.id === instanceId || props.item.instanceId === instanceId)
      ) {
        return props.item;
      }

      return (props.inventoryItems || []).find((invItem) => {
        return invItem?.id === instanceId || invItem?.instanceId === instanceId;
      });
    };

    const syncItemData = () => {
      const robotItem = findRobotItem();

      if (!robotItem) {
        if (!data.value?.instanceId) {
          showNotification('ไม่พบไอเทม', 'error');
          emit('close');
        }
        return;
      }

      data.value = normalizeRobotItem(robotItem);
    };

    const applyLocalPatch = (patch = {}) => {
      const currentStates = asObject(data.value.states);

      const nextStates = {
        ...currentStates,
        ...patch,
      };

      data.value = normalizeRobotItem({
        ...data.value,
        ...patch,
        states: nextStates,
      });
    };

    const showNotification = (message, type = 'success') => {
      notificationMessage.value = message;
      notificationType.value = type;

      setTimeout(() => {
        notificationMessage.value = '';
      }, 3000);
    };

    const subscribeBagItemRealtime = () => {
      if (unsubscribeBagItem) {
        unsubscribeBagItem();
        unsubscribeBagItem = null;
      }

      if (!user.value?.uid) return;

      const instanceId = getCurrentInstanceId();
      if (!instanceId) return;

      unsubscribeBagItem = projectFirestore
        .collection('bags')
        .doc(user.value.uid)
        .onSnapshot((doc) => {
          if (!doc.exists) return;

          const bagData = doc.data() || {};
          const bagItem = bagData.items?.[instanceId];

          if (!bagItem) return;

          data.value = normalizeRobotItem({
            ...bagItem,
            id: instanceId,
            instanceId,
          });
        });
    };

    const allowedMaps = computed(() => {
      return robotMap.find((robot) => robot.name === data.value.name)?.map || [];
    });

    const isDeployed = computed(() => {
      return !!data.value.map;
    });

    const statList = [
      { name: 'stamina', icon: 'fas fa-heart' },
      { name: 'str', icon: 'fas fa-dumbbell' },
      { name: 'int', icon: 'fas fa-brain' },
      { name: 'agi', icon: 'fas fa-running' },
    ];

    const totalPoints = computed(() => {
      return (
        num(points.value.stamina, 0) +
        num(points.value.str, 0) +
        num(points.value.int, 0) +
        num(points.value.agi, 0)
      );
    });

    const remainingUpgrades = computed(() => {
      return Math.max(
        0,
        num(data.value.maxUpgradeCount, 0) - num(data.value.upgradeCount, 0)
      );
    });

    const statPoints = computed(() => {
      return num(data.value.statPoints, 0);
    });

    const isRedistributeMode = computed(() => {
      return statPoints.value > 0;
    });

    const materialCounts = computed(() => {
      const counts = {};
      const currentInstanceId = getCurrentInstanceId();

      (props.inventoryItems || []).forEach((invItem) => {
        if (!invItem) return;

        const instanceId = invItem.instanceId || invItem.id;
        if (instanceId === currentInstanceId) return;
        if (invItem.states?.locked) return;

        const template = getTemplate(invItem);

        const names = [
          invItem.templateId,
          invItem.name,
          template.name,
        ].filter(Boolean);

        [...new Set(names)].forEach((name) => {
          counts[name] = (counts[name] || 0) + 1;
        });
      });

      return counts;
    });

    const getMaterialCount = (itemName) => {
      return materialCounts.value[itemName] || 0;
    };

    const maxPossibleUpgrades = computed(() => {
      const materials = asArray(data.value.upgradeMaterials);

      if (remainingUpgrades.value <= 0) return 0;
      if (materials.length === 0) return 0;

      let minFromMaterial = Infinity;

      for (const material of materials) {
        const available = getMaterialCount(material.itemName);
        const perUpgrade = num(material.quantity, 0);

        if (perUpgrade <= 0) return 0;

        const possible = Math.floor(available / perUpgrade);
        minFromMaterial = Math.min(minFromMaterial, possible);
      }

      if (!Number.isFinite(minFromMaterial)) return 0;

      return Math.max(0, Math.min(minFromMaterial, remainingUpgrades.value));
    });

    const requiredPoints = computed(() => {
      return isRedistributeMode.value
        ? statPoints.value
        : maxPossibleUpgrades.value * 10;
    });

    const hasEnoughMaterialsForMulti = computed(() => {
      if (maxPossibleUpgrades.value <= 0) return false;

      return asArray(data.value.upgradeMaterials).every((material) => {
        return getMaterialCount(material.itemName) >= num(material.quantity, 0) * maxPossibleUpgrades.value;
      });
    });

    const hasEnoughForOne = (material) => {
      return getMaterialCount(material.itemName) >= num(material.quantity, 0);
    };

    const totalItemPoints = computed(() => {
      const itemStats = normalizeStats(data.value.itemUpgradeStats || {});

      return (
        num(itemStats.stamina, 0) +
        num(itemStats.str, 0) +
        num(itemStats.int, 0) +
        num(itemStats.agi, 0)
      );
    });

    const purchasedSilverUpgrades = computed(() => {
      return asArray(data.value.silverUpgrades);
    });

    const purchasedSilverUpgradeSet = computed(() => {
      return new Set(purchasedSilverUpgrades.value.map((id) => String(id)));
    });

    const isSilverUpgradePurchased = (upgradeId) => {
      return purchasedSilverUpgradeSet.value.has(String(upgradeId));
    };

    const calculatedStats = computed(() => {
      const baseStats = normalizeStats(data.value.stats || {});
      const itemStats = normalizeStats(data.value.itemUpgradeStats || {});
      const silverStats = normalizeStats(data.value.silverUpgradeStats || {});

      const totalStats = mergeStats(baseStats, itemStats, silverStats);

      return {
        maxHp: totalStats.stamina * 20 + totalStats.maxHp,
        atk: totalStats.str * 2 + totalStats.atk,
        critical: Math.floor(totalStats.int / 2) + totalStats.critical,
        evasion: Math.floor(totalStats.agi / 2) + totalStats.evasion,
        defense: totalStats.defense,
      };
    });

    const currentHp = computed(() => {
      return Math.max(0, calculatedStats.value.maxHp - num(data.value.damageTaken, 0));
    });

    const hpPercentage = computed(() => {
      if (!calculatedStats.value.maxHp) return 0;

      return Math.max(
        0,
        Math.min(100, (currentHp.value / calculatedStats.value.maxHp) * 100)
      );
    });

    const previewStats = computed(() => {
      const baseStats = normalizeStats(data.value.stats || {});
      const itemStats = normalizeStats(data.value.itemUpgradeStats || {});
      const silverStats = normalizeStats(data.value.silverUpgradeStats || {});

      let nextItemStats = { ...itemStats };

      if (isRedistributeMode.value) {
        nextItemStats = {
          ...nextItemStats,
          stamina: num(points.value.stamina, 0),
          str: num(points.value.str, 0),
          int: num(points.value.int, 0),
          agi: num(points.value.agi, 0),
        };
      } else {
        nextItemStats = {
          ...nextItemStats,
          stamina: num(nextItemStats.stamina, 0) + num(points.value.stamina, 0),
          str: num(nextItemStats.str, 0) + num(points.value.str, 0),
          int: num(nextItemStats.int, 0) + num(points.value.int, 0),
          agi: num(nextItemStats.agi, 0) + num(points.value.agi, 0),
        };
      }

      const totalStats = mergeStats(baseStats, nextItemStats, silverStats);

      return {
        maxHp: totalStats.stamina * 20 + totalStats.maxHp,
        atk: totalStats.str * 2 + totalStats.atk,
        critical: Math.floor(totalStats.int / 2) + totalStats.critical,
        evasion: Math.floor(totalStats.agi / 2) + totalStats.evasion,
        defense: totalStats.defense,
      };
    });

    const handleImageError = (event) => {
      event.target.src = defaultImg;
    };

    const clampPoint = (stat) => {
      const value = Math.floor(num(points.value[stat], 0));
      points.value[stat] = Math.max(0, value);
    };

    const increasePoint = (stat) => {
      if (totalPoints.value < requiredPoints.value) {
        points.value[stat] = num(points.value[stat], 0) + 1;
      }
    };

    const decreasePoint = (stat) => {
      if (num(points.value[stat], 0) > 0) {
        points.value[stat] = num(points.value[stat], 0) - 1;
      }
    };

    const resetPoints = () => {
      points.value = { stamina: 0, str: 0, int: 0, agi: 0 };
    };

    const handleUpgradeOrRedistribute = async () => {
      if (totalPoints.value !== requiredPoints.value) return;

      if (isRedistributeMode.value) {
        await redistributeRobot();
      } else {
        if (!hasEnoughMaterialsForMulti.value) return;
        await upgradeRobot();
      }
    };

    const upgradeRobot = async () => {
      isUpgrading.value = true;

      const pointsPayload = {
        stamina: num(points.value.stamina, 0),
        str: num(points.value.str, 0),
        int: num(points.value.int, 0),
        agi: num(points.value.agi, 0),
      };

      try {
        const result = await callManageKillerRobot('upgrade', {
          points: pointsPayload,
        });

        const currentItemStats = normalizeStats(data.value.itemUpgradeStats || {});
        const numUpgrades = totalPoints.value / 10;

        applyLocalPatch({
          itemUpgradeStats: {
            ...currentItemStats,
            stamina: currentItemStats.stamina + pointsPayload.stamina,
            str: currentItemStats.str + pointsPayload.str,
            int: currentItemStats.int + pointsPayload.int,
            agi: currentItemStats.agi + pointsPayload.agi,
          },
          rarity: num(data.value.rarity, 1) + numUpgrades,
          upgradeCount: num(data.value.upgradeCount, 0) + numUpgrades,
        });

        resetPoints();
        showNotification(result?.data?.message || 'อัปเกรดหุ่นยนต์สำเร็จ!', 'success');
      } catch (err) {
        console.error('Error upgrading robot:', err);
        showNotification(err?.message || 'เกิดข้อผิดพลาดในการอัปเกรด', 'error');
      } finally {
        isUpgrading.value = false;
      }
    };

    const redistributeRobot = async () => {
      isUpgrading.value = true;

      const pointsPayload = {
        stamina: num(points.value.stamina, 0),
        str: num(points.value.str, 0),
        int: num(points.value.int, 0),
        agi: num(points.value.agi, 0),
      };

      try {
        await callManageKillerRobot('redistribute', {
          points: pointsPayload,
        });

        const currentItemStats = normalizeStats(data.value.itemUpgradeStats || {});

        applyLocalPatch({
          itemUpgradeStats: {
            ...currentItemStats,
            stamina: pointsPayload.stamina,
            str: pointsPayload.str,
            int: pointsPayload.int,
            agi: pointsPayload.agi,
          },
          statPoints: 0,
        });

        resetPoints();
        showNotification('กระจายสเตตัสใหม่สำเร็จ!', 'success');
      } catch (err) {
        console.error('Error redistributing robot:', err);
        showNotification(err?.message || 'เกิดข้อผิดพลาดในการกระจายสเตตัสใหม่', 'error');
      } finally {
        isUpgrading.value = false;
      }
    };

    const resetRobotStats = async () => {
      isResetting.value = true;

      try {
        await callManageKillerRobot('reset');

        const itemStats = normalizeStats(data.value.itemUpgradeStats || {});
        const totalResetPoints =
          num(itemStats.stamina, 0) +
          num(itemStats.str, 0) +
          num(itemStats.int, 0) +
          num(itemStats.agi, 0);

        applyLocalPatch({
          itemUpgradeStats: {
            ...itemStats,
            stamina: 0,
            str: 0,
            int: 0,
            agi: 0,
          },
          statPoints: totalResetPoints,
        });

        resetPoints();
        showNotification('รีเซ็ตสเตตัสสำเร็จ! สามารถกระจายแต้มใหม่ได้', 'success');
      } catch (err) {
        console.error('Error resetting robot stats:', err);
        showNotification(err?.message || 'เกิดข้อผิดพลาดในการรีเซ็ตสเตตัส', 'error');
      } finally {
        isResetting.value = false;
      }
    };

    const confirmDeploy = () => {
      deployRobot();
    };

    const deployRobot = async () => {
      if (!selectedMap.value) return;

      isDeploying.value = true;

      try {
        const result = await callManageKillerRobot('deploy', {
          mapName: selectedMap.value,
        });

        applyLocalPatch({
          map: selectedMap.value,
          killerRobotId: result?.data?.killerRobotId || null,
        });

        showNotification('ใช้งานหุ่นยนต์สำเร็จ!', 'success');

        emit('robot-updated');
        emit('close');
      } catch (err) {
        console.error('Error deploying robot:', err);
        showNotification(err?.message || 'เกิดข้อผิดพลาดในการใช้งานหุ่นยนต์', 'error');
      } finally {
        isDeploying.value = false;
      }
    };

    const recallRobot = async () => {
      isRecalling.value = true;

      try {
        await callManageKillerRobot('recall', {
          killerRobotId: data.value.killerRobotId || data.value.id || getCurrentInstanceId(),
        });

        showNotification('เรียกคืนหุ่นยนต์สำเร็จ!', 'success');

        emit('robot-updated');
        emit('close');
      } catch (err) {
        console.error('Error recalling robot:', err);
        showNotification(err?.message || 'เกิดข้อผิดพลาดในการเรียกคืนหุ่นยนต์', 'error');
      } finally {
        isRecalling.value = false;
      }
    };

    const repairRobot = async () => {
      isRepairing.value = true;

      try {
        await callManageKillerRobot('repair');

        applyLocalPatch({
          damageTaken: 0,
        });

        showNotification('รักษาหุ่นยนต์สำเร็จ!', 'success');
      } catch (err) {
        console.error('Error repairing robot:', err);
        showNotification(err?.message || 'เกิดข้อผิดพลาดในการรักษาหุ่นยนต์', 'error');
      } finally {
        isRepairing.value = false;
      }
    };

    const purchaseSilverUpgrade = async (upgrade) => {
      isPurchasing.value[upgrade.id] = true;

      const currentPurchasedBefore = asArray(data.value.silverUpgrades);
      const currentSilverStatsBefore = normalizeStats(data.value.silverUpgradeStats || {});

      const optimisticPurchased = currentPurchasedBefore.some(
        (id) => String(id) === String(upgrade.id)
      )
        ? currentPurchasedBefore
        : [...currentPurchasedBefore, upgrade.id];

      const optimisticSilverStats = { ...currentSilverStatsBefore };

      Object.entries(upgrade.effect || {}).forEach(([stat, value]) => {
        optimisticSilverStats[stat] =
          num(optimisticSilverStats[stat], 0) + num(value, 0);
      });

      try {
        /*
          Optimistic update:
          ให้ UI เปลี่ยนเป็น "อัปเกรดแล้ว" ทันที
          ไม่ต้องรอ onSnapshot
        */
        applyLocalPatch({
          silverUpgradeStats: optimisticSilverStats,
          silverUpgrades: optimisticPurchased,
        });

        silver.value = Math.max(0, num(silver.value, 0) - num(upgrade.cost, 0));

        await callManageKillerRobot('silverUpgrade', {
          upgradeType: upgrade.id,
          upgrades: silverUpgrades,
        });

        showNotification('อัปเกรดด้วย silver สำเร็จ!', 'success');
      } catch (err) {
        console.error('Error purchasing upgrade:', err);

        /*
          Rollback ถ้า server reject
        */
        applyLocalPatch({
          silverUpgradeStats: currentSilverStatsBefore,
          silverUpgrades: currentPurchasedBefore,
        });

        silver.value = num(silver.value, 0) + num(upgrade.cost, 0);

        showNotification(err?.message || 'เกิดข้อผิดพลาดในการอัปเกรดด้วย silver', 'error');
      } finally {
        isPurchasing.value[upgrade.id] = false;
      }
    };

    onMounted(() => {
      syncItemData();
      subscribeBagItemRealtime();

      if (user.value?.uid) {
        unsubscribeSilver = projectFirestore
          .collection('characters')
          .doc(user.value.uid)
          .onSnapshot((doc) => {
            if (doc.exists) {
              silver.value = num(doc.data().silver, 0);
            }
          });
      }
    });

    watch(() => props.item, () => {
      syncItemData();
      subscribeBagItemRealtime();
    }, { deep: true });

    watch(() => props.states, () => {
      /*
        ไม่ให้ props.states เก่าทับ optimistic local state
        แต่ยัง sync เมื่อ parent ส่ง item ใหม่เข้ามา
      */
      if (!data.value?.instanceId) {
        syncItemData();
      }
    }, { deep: true });

    watch(() => props.inventoryItems, () => {
      /*
        sync จาก inventoryItems เฉพาะตอนยังไม่มี data
        เพราะข้อมูล real-time หลักมาจาก bags/{uid}.items.{instanceId}
      */
      if (!data.value?.instanceId) {
        syncItemData();
      }
    }, { deep: true });

    watch(() => props.itemId, () => {
      syncItemData();
      subscribeBagItemRealtime();
    });

    onUnmounted(() => {
      if (unsubscribeSilver) unsubscribeSilver();
      if (unsubscribeBagItem) unsubscribeBagItem();
    });

    return {
      activeTab,
      upgradeType,
      points,

      isUpgrading,
      isResetting,
      isDeploying,
      isRecalling,
      isRepairing,
      isPurchasing,

      selectedMap,
      data,
      defaultImg,

      isDeployed,
      statList,
      totalPoints,
      remainingUpgrades,
      calculatedStats,
      currentHp,
      hpPercentage,
      previewStats,

      hasEnoughMaterialsForMulti,
      hasEnoughForOne,
      getMaterialCount,

      handleImageError,
      increasePoint,
      decreasePoint,
      clampPoint,

      handleUpgradeOrRedistribute,
      resetRobotStats,
      confirmDeploy,
      deployRobot,
      recallRobot,
      repairRobot,

      notificationMessage,
      notificationType,

      silver,
      silverUpgrades,
      purchasedSilverUpgrades,
      isSilverUpgradePurchased,
      purchaseSilverUpgrade,

      allowedMaps,
      isRedistributeMode,
      statPoints,
      requiredPoints,
      maxPossibleUpgrades,
      totalItemPoints,
    };
  },
};
</script>

<style scoped>
/* Custom Scrollbar */
.scrollbar-thin::-webkit-scrollbar {
  width: 5px;
}

.scrollbar-thin::-webkit-scrollbar-track {
  background: #2d3748;
  border-radius: 10px;
}

.scrollbar-thin::-webkit-scrollbar-thumb {
  background: #4a5568;
  border-radius: 10px;
}

.scrollbar-thin::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}

.scrollbar-thin {
  scrollbar-width: thin;
  scrollbar-color: #4a5568 #2d3748;
}

/* Animations */
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

@keyframes modalFade {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

.animate-modalFade {
  animation: modalFade 0.3s ease-out;
}

@keyframes scaleIn {
  from {
    transform: scale(0.95);
    opacity: 0;
  }

  to {
    transform: scale(1);
    opacity: 1;
  }
}

.animate-scaleIn {
  animation: scaleIn 0.3s ease-out;
}

button:not(:disabled):active {
  transform: scale(0.98);
}

/* Hide arrows in number input */
input[type='number']::-webkit-outer-spin-button,
input[type='number']::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type='number'] {
  -moz-appearance: textfield;
}
</style>